import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, reload, sendEmailVerification } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc, query, where, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, functions } from '../../firebase';
import logo from '../images/images.png';

const normalizePhoneNumber = (phone) => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('1')) return digits;
  return '1' + digits;
};

const BrokerSignup = () => {
  const [searchParams] = useSearchParams();
  const teamBrokerId = searchParams.get('team'); // If joining a team
  const inviteEmail = searchParams.get('email') || '';
  const [parentBrokerName, setParentBrokerName] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: inviteEmail,
    password: '',
    phoneNumber: '',
    brokerageName: '',
    businessBroker: true,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [website, setWebsite] = useState('');
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const openedAtRef = useRef(Date.now());
  const logoInputRef = useRef(null);
  const navigate = useNavigate();

  // If joining a team, fetch parent broker info
  useEffect(() => {
    if (teamBrokerId) {
      getDoc(doc(db, 'brokers', teamBrokerId)).then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          const name = d.brokerageName || `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'your team';
          setParentBrokerName(name);
          if (d.brokerageName) setFormData(prev => ({ ...prev, brokerageName: d.brokerageName }));
        }
      }).catch(() => {});
    }
  }, [teamBrokerId]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      alert('You must agree to the terms of service.');
      return;
    }
    setLoading(true);
    setError(null);

    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    const reasonableName = value => /^[A-Za-z][A-Za-z .'-]{1,49}$/.test(value.trim());
    if (website || Date.now() - openedAtRef.current < 2500) {
      setError('We could not verify this signup. Please refresh and try again.');
      setLoading(false);
      return;
    }
    if (!reasonableName(formData.firstName) || !reasonableName(formData.lastName) || phoneDigits.length < 10 || phoneDigits.length > 15) {
      setError('Please enter a valid name and phone number.');
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // Do not create a broker record, send emails, or trigger CRM automation
      // until the applicant proves they control the email address.
      await sendEmailVerification(user, { url: 'https://lended.ai/broker-signup' });
      sessionStorage.setItem('pendingBrokerSignup', JSON.stringify({
        firstName: formData.firstName.trim(), lastName: formData.lastName.trim(),
        email: formData.email.toLowerCase().trim(), phoneNumber: formData.phoneNumber,
        brokerageName: formData.brokerageName.trim(), businessBroker: formData.businessBroker,
        teamBrokerId: teamBrokerId || '',
      }));
      setAwaitingVerification(true);
      setVerificationMessage(`We sent a verification link to ${formData.email}.`);
      setLoading(false);
      return;

      // Save broker data to the 'brokers' collection FIRST, before any optional
      // steps (logo upload, parent linking). This guarantees an account is never
      // left with a login but no broker profile — which would hang the portal.
      const normalizedPhone = normalizePhoneNumber(formData.phoneNumber);
      const brokerData = {
        createdAt: serverTimestamp(),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        brokerageName: formData.brokerageName || null,
        lastUpdated: serverTimestamp(),
        phoneNumber: normalizedPhone,
        userId: user.uid,
        prequalificationApplications: [],
        businessBroker: formData.businessBroker,
        // If joining a team, link to parent broker so they show as a sub-member.
        ...(teamBrokerId && { parentBrokerId: teamBrokerId }),
      };
      await setDoc(doc(db, 'brokers', user.uid), brokerData);

      // If joining a team, add this user to the parent's team members list.
      if (teamBrokerId) {
        try {
          await updateDoc(doc(db, 'brokers', teamBrokerId), {
            teamMembers: arrayUnion({ uid: user.uid, email: formData.email, firstName: formData.firstName, lastName: formData.lastName, joinedAt: new Date().toISOString() }),
          });
        } catch (e) { console.error('Error adding to parent team:', e); }
      }

      // Upload logo if provided, then attach it to the (already-created) profile.
      if (logoFile) {
        try {
          const storage = getStorage();
          const storageRefPath = ref(storage, `brokerLogos/${user.uid}`);
          await uploadBytes(storageRefPath, logoFile);
          const logoUrl = await getDownloadURL(storageRefPath);
          await updateDoc(doc(db, 'brokers', user.uid), { logoUrl });
        } catch (e) { console.error('Error uploading broker logo (account still created):', e); }
      }

      // Mark any matching pending broker as signed up
      try {
        const pendingSnap = await getDocs(query(
          collection(db, 'pendingBrokerSignups'),
          where('email', '==', formData.email.toLowerCase().trim())
        ));
        for (const pendingDoc of pendingSnap.docs) {
          await updateDoc(doc(db, 'pendingBrokerSignups', pendingDoc.id), {
            signedUp: true,
            signedUpAt: serverTimestamp(),
            brokerId: user.uid,
            autoDetected: true,
          });
        }
        if (pendingSnap.docs.length > 0) console.log('Marked pending broker as signed up');
      } catch (e) { console.error('Error marking pending broker:', e); }

      // Send welcome email from Isak
      try {
        await addDoc(collection(db, 'email'), {
          to: formData.email,
          from: 'Isak @ Lended <isak@lended.ai>',
          replyTo: 'isak@lended.ai',
          subject: 'Welcome to Lended — Let\'s Get Your Listings Prequalified',
          message: {
            subject: 'Welcome to Lended — Let\'s Get Your Listings Prequalified',
            html: `<p>Hi ${formData.firstName},</p>
<p>Welcome to Lended! Your broker portal account has been created and you're all set.</p>
<p>Here's what happens next:</p>
<ol>
  <li>Upload a listing with the business's financial documents</li>
  <li>We'll turn around a prequalification within 24 hours</li>
  <li>Share the prequal with interested buyers</li>
</ol>
<p>You can log in anytime to manage your listings:</p>
<p><a href="https://lended.ai/broker-portal" style="display:inline-block;padding:10px 24px;background:#4f46e5;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Go to Broker Portal</a></p>
<p>I'm here to help with anything you need. Don't hesitate to reach out!</p>
<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`,
          },
        });
        console.log('Broker welcome email sent');
      } catch (e) { console.error('Error sending broker welcome email:', e); }

      // Trigger GHL broker signed-up webhook
      try {
        await fetch('https://services.leadconnectorhq.com/hooks/jt2cla2yKK13pavtLwNJ/webhook-trigger/CiQlqr5DElGaXLY07f4E', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: normalizedPhone,
          }),
        });
        console.log('GHL broker signed-up webhook triggered');
      } catch (e) { console.error('Error triggering GHL webhook:', e); }

      // Send notification to Max
      try {
        await addDoc(collection(db, 'email'), {
          to: 'max@sbacapital.ai',
          from: 'Isak @ Lended <isak@lended.ai>',
          replyTo: 'isak@lended.ai',
          subject: 'New Broker Sign Up - ' + formData.firstName + ' ' + formData.lastName,
          message: {
            subject: 'New Broker Sign Up',
            html: `<p>A new broker has signed up:</p><ul><li>Name: ${formData.firstName} ${formData.lastName}</li><li>Email: ${formData.email}</li><li>Phone: ${formData.phoneNumber}</li><li>Brokerage: ${formData.brokerageName || 'N/A'}</li></ul>`,
          },
        });
      } catch (e) { console.error('Error sending signup notification:', e); }

      // Redirect to broker portal
      navigate('/broker-portal');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const finishVerifiedSignup = async () => {
    setLoading(true);
    setError(null);
    setVerificationMessage('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Your signup session expired. Please sign up again.');
      await reload(user);
      if (!user.emailVerified) {
        setVerificationMessage('That email is not verified yet. Open the link in your inbox, then try again.');
        return;
      }
      await user.getIdToken(true);
      const pending = JSON.parse(sessionStorage.getItem('pendingBrokerSignup') || '{}');
      const completeBrokerSignup = httpsCallable(functions, 'completeBrokerSignup');
      await completeBrokerSignup(pending);

      if (logoFile) {
        const storage = getStorage();
        const logoRef = ref(storage, `brokerLogos/${user.uid}`);
        await uploadBytes(logoRef, logoFile);
        const logoUrl = await getDownloadURL(logoRef);
        await updateDoc(doc(db, 'brokers', user.uid), { logoUrl });
      }
      sessionStorage.removeItem('pendingBrokerSignup');
      navigate('/broker-portal');
    } catch (signupError) {
      setError(signupError.message || 'Could not finish signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    try {
      await sendEmailVerification(auth.currentUser, { url: 'https://lended.ai/broker-signup' });
      setVerificationMessage('A new verification link was sent.');
    } catch (resendError) {
      setError(resendError.message);
    } finally {
      setLoading(false);
    }
  };

  if (awaitingVerification) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4"><div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl"><img className="mx-auto h-12 w-auto" src={logo} alt="Lended" /><div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-2xl">✉️</div><h1 className="mt-4 text-2xl font-bold text-gray-900">Verify your email</h1><p className="mt-2 text-sm leading-6 text-gray-600">Your broker portal will only be created after email verification.</p>{verificationMessage && <p className="mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-800">{verificationMessage}</p>}{error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}<button type="button" onClick={finishVerifiedSignup} disabled={loading} className="mt-6 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{loading ? 'Checking…' : 'I verified my email'}</button><button type="button" onClick={resendVerification} disabled={loading} className="mt-3 text-sm font-semibold text-blue-600 hover:underline disabled:opacity-50">Resend verification email</button></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-blue-900 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-white p-10 rounded-xl shadow-2xl">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src={logo}
            alt="Your Company"
          />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            {teamBrokerId ? `Join ${parentBrokerName || 'your team'}` : 'Sign up as a Partner'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {teamBrokerId ? 'Create your account to access the shared broker portal.' : 'Create an account to start connecting businesses with lenders.'}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="hidden" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" type="text" tabIndex="-1" autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} /></div>
          <div className="rounded-md shadow-sm space-y-4">
            <div className="flex space-x-4">
              <div className="w-1/2">
                <label htmlFor="firstName" className="sr-only">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="First Name"
                  onChange={handleChange}
                />
              </div>
              <div className="w-1/2">
                <label htmlFor="lastName" className="sr-only">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Last Name"
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label htmlFor="phoneNumber" className="sr-only">Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                id="phoneNumber"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Phone Number"
                onChange={handleChange}
              />
            </div>
            {!teamBrokerId && (
              <>
                <div>
                  <label htmlFor="brokerageName" className="sr-only">Brokerage Name</label>
                  <input
                    type="text"
                    name="brokerageName"
                    id="brokerageName"
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Brokerage Name (optional)"
                    onChange={handleChange}
                  />
                </div>
                {/* Logo Upload */}
                <div className="border border-gray-300 rounded-md p-3">
                  <label className="block text-sm text-gray-700 mb-2">Brokerage Logo (optional)</label>
                  <div className="flex items-center gap-3">
                    {logoPreview ? (
                      <div className="relative">
                        <img src={logoPreview} alt="Logo preview" className="h-12 w-12 object-contain border border-gray-200 rounded" />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="h-12 w-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <input
                      type="file"
                      ref={logoInputRef}
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="cursor-pointer text-sm text-blue-600 hover:text-blue-700"
                    >
                      {logoPreview ? 'Change logo' : 'Upload logo'}
                    </label>
                  </div>
                </div>
              </>
            )}
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                className={`appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm ${teamBrokerId && inviteEmail ? 'bg-gray-100' : ''}`}
                placeholder="Email address"
                readOnly={!!(teamBrokerId && inviteEmail)}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                type="password"
                name="password"
                id="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                onChange={handleChange}
              />
            </div>
            <div className="flex items-center">
              <input
                id="termsAccepted"
                name="termsAccepted"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-900">
                I agree to the <button type="button" onClick={() => setShowTerms(true)} className="text-blue-600 underline">terms of service</button>
              </label>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? 'Signing up...' : 'Sign up'}
            </button>
          </div>
        </form>
      </div>
      {showTerms && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Terms of Service</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p>Effective Date: {new Date().toLocaleDateString()}</p>
              <p>
                By using our services, you agree to our terms and conditions for business brokers.
              </p>
              <p>
                We are committed to providing you with the best possible experience and will always strive to act in your best interest.
              </p>
              <p>
                Your information will be handled with utmost confidentiality and used only for the purpose of connecting businesses with potential buyers.
              </p>
              <p>
                For any questions or concerns regarding these terms, please contact:
              </p>
              <p>
                Max Krakow, CEO<br />
                Phone: (914) 319-7160
              </p>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerSignup;
